/*======================================================================================================================
  * Bluebell: src/app/state/results-view/results-view.interfaces.ts
  * Last Edited: 30/07/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { TextColumn } from '@state/text-columns/text-columns.interfaces';


export interface ResultsViewListData {
  uuid: string;
  description: string;
  last_used: boolean;
  programme: string;
  programme_name: string;
  user: string;
  pinned_in_positions: { project: string, position: number }[];
  default_for_projects: string[];
}

export interface ResultsView extends ResultsViewListData {
  text_columns: TextColumn[];
  data: ResultsViewData;
}

export interface ResultsViewData {
  columns?: ResultsColumnConfig[];
  deltaBaseline?: ResultsViewDeltaBaseline;
  deltaToLeadCase?: boolean;
  sortData?: ResultsColumnSortData[];
  hideEditColumn?: boolean;
}

export interface ResultsColumnSortData {
  field: string;
  type: ResultsTableColumnType;
  index: number;
  hidden?: boolean;
  minimised?: boolean;
  wrapped?: boolean;
  frozen?: boolean;
  icon?: string;
}

export type DeltaBaselineType = 'Run' | 'Attitude' | 'Map Relative';

export interface ResultsViewDeltaBaseline {
  type: DeltaBaselineType;
  uuid: string;
  name?: string | null | undefined;
}


export interface ResultsColumnConfig {
  displayName: string;
  property: string;
  negation: boolean;  // Finding the additive inverse of a number
  percentage: boolean;  // display as percentage
  percentageDelta: boolean; // display delta as percentage change
  highlight: boolean;
  colorScale: string;
  delta: string;
  roundingType: string;
  roundingDigits: number;
  scalingFactor: number; // scale all absolute and delta values by the factor input
  mapParameter: boolean;
  parameter: boolean;
  calculation: boolean;
}

export type ResultsTableColumnType = 'standard' | 'parameter' | 'mapParameter' | 'monitor' | 'text' | 'calculation';
export interface ResultsTableColumn {
  header: string;
  field: string;
  type: ResultsTableColumnType;
  hidden: boolean;
  frozen: boolean;
  minimisable: boolean;
  minimised: boolean;
  wrappable: boolean;
  wrapped: boolean;
  icon?: string;
  uuid?: string; // needed for text type cols
  colorScale?: string;
}

export interface SortedResultsTableColumn extends ResultsTableColumn {
  index: number;
}

export interface ListResultsViewOptions {
  sandbox?: string;
  programme?: string;
}

export interface ListResultsViewResponse {
  data: ResultsViewListData[];
  current_default: string | null;
}

export type UpdateResultsViewPartial = Pick<ResultsView, 'uuid'> & Partial<ResultsView>;
// export type ResultsViewCreatePartial = Pick<ResultsView, 'programme'|'pinned_in_positions'|'data'|'text_columns'|'description'|'last_used'>;
export type ResultsViewCreatePartial = Omit<ResultsView, 'uuid'>;

export const defaultStandardColumns: ResultsTableColumn[] = [
  {
    header: 'Name',
    field: 'name',
    type: 'standard',
    hidden: false,
    frozen: true,
    minimisable: true,
    minimised: false,
    wrappable: false,
    wrapped: false,
  },
  {
    header: 'Description',
    field: 'description',
    type: 'standard',
    minimisable: true,
    minimised: false,
    frozen: true,
    hidden: false,
    wrappable: false,
    wrapped: false,
  },
  {
    header: 'Status',
    field: 'status',
    type: 'standard',
    minimisable: true,
    minimised: false,
    wrappable: false,
    wrapped: false,
    frozen: false,
    hidden: false,
  },
  {
    header: 'Reports',
    field: 'reports',
    type: 'standard',
    minimisable: true,
    minimised: true,
    wrappable: false,
    wrapped: false,
    frozen: false,
    hidden: false,
  },
  {
    header: 'Comment',
    field: 'comment',
    type: 'standard',
    minimisable: true,
    minimised: true,
    wrappable: true,
    wrapped: false,
    frozen: false,
    hidden: false,
  },
  {
    header: 'Annotations',
    field: 'hasAnnotations',
    type: 'standard',
    minimisable: true,
    minimised: false,
    wrappable: false,
    wrapped: false,
    frozen: false,
    hidden: false,
  }
];

export const defaultStandardColumnSortData = defaultStandardColumns
  .map(({ field, type }, index) => ({ field, type, index }));
